import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, type AdminIntegration, type ProviderStatus } from "../api";
import { useApiData } from "../hooks";
import { Empty, ErrorNote, Header, Loading, Note, SectionLabel, formatWhen } from "../ui";

interface WebhookEvent {
  id: string;
  source: string;
  eventType: string;
  processingStatus: string;
  errorMessage: string | null;
  receivedAt: string;
  processedAt: string | null;
}

interface Reconciliation {
  intentId: string;
  userId: string;
  productId: string;
  status: string;
  failureReason: string | null;
  createdAt: string;
}

/**
 * Admin hub: one card per admin area, so the admin functions are discoverable and navigable rather
 * than stacked on a single invisible page. Cards for areas with actionable counts (integrations,
 * reconciliation) show a light summary; the rest are a description and a link.
 */
export function AdminHubPage(): JSX.Element {
  return (
    <>
      <Header title="Admin" back="/menu" backLabel={"\u2190 Menu"} />
      <div className="content">
        <SectionLabel>Operations</SectionLabel>
        <ul className="list">
          <HubCard
            to="/admin/integrations"
            title="Integrations"
            description="Define and publish outbound notification integrations."
          />
          <HubCard
            to="/admin/reconciliation"
            title="Reconciliation"
            description="Purchases that were paid but never provisioned."
          />
          <HubCard
            to="/admin/lifecycle"
            title="Subscription lifecycle"
            description="Run the grace-expiry sweep."
          />
          <HubCard
            to="/admin/providers"
            title="Provider status"
            description="Communication, payment and push configuration."
          />
          <HubCard
            to="/admin/webhooks"
            title="Inbound webhooks"
            description="Recent provider webhooks and their processing status."
          />
        </ul>

        <SectionLabel>Management</SectionLabel>
        <ul className="list">
          <HubCard
            to="/admin/subscriptions"
            title="Subscriptions"
            description="Edit subscriptions: renewal, term, product and linked number."
          />
          <HubCard
            to="/admin/numbers"
            title="Numbers"
            description="All numbers across users, with their subscription and owner."
          />
        </ul>
      </div>
    </>
  );
}

function HubCard({
  to,
  title,
  description
}: {
  to: string;
  title: string;
  description: string;
}): JSX.Element {
  return (
    <li>
      <div className="row-main">
        <div className="row-title">
          <Link className="back" to={to}>
            {title}
          </Link>
        </div>
        <div className="row-sub">{description}</div>
      </div>
    </li>
  );
}

/** Define and publish outbound integrations, and remove them. */
export function AdminIntegrationsPage(): JSX.Element {
  const { data: integrations, error, reload } =
    useApiData<AdminIntegration[]>("/api/v1/admin/integrations");
  const [actionError, setActionError] = useState<unknown>(null);

  async function publish(integrationId: string, status: string): Promise<void> {
    setActionError(null);
    try {
      await api.put(`/api/v1/admin/integrations/${integrationId}`, { status });
      reload();
    } catch (caught) {
      setActionError(caught);
    }
  }

  return (
    <>
      <Header
        title="Integrations"
        back="/admin"
        backLabel={"\u2190 Admin"}
        action={
          <Link className="action" to="/admin/integrations/new" title="Define integration">
            +
          </Link>
        }
      />
      <div className="content">
        <ErrorNote error={error} />
        <ErrorNote error={actionError} />
        {integrations === null ? (
          <Loading />
        ) : integrations.length === 0 ? (
          <Empty>No integrations defined.</Empty>
        ) : (
          <ul className="list">
            {integrations.map((integration) => (
              <li key={integration.id}>
                <div className="avatar sq">{integration.name.slice(0, 2).toUpperCase()}</div>
                <div className="row-main">
                  <div className="row-title">{integration.name}</div>
                  <div className="row-sub">
                    {integration.status === "PUBLISHED"
                      ? `${integration.numbersEnabled} numbers enabled`
                      : integration.status.toLowerCase()}
                  </div>
                  <div className="row-sub">{integration.endpointUrl}</div>
                </div>
                <div className="row-actions">
                  <button
                    type="button"
                    onClick={() =>
                      publish(integration.id, integration.status === "PUBLISHED" ? "RETIRED" : "PUBLISHED")
                    }
                  >
                    {integration.status === "PUBLISHED" ? "Retire" : "Publish"}
                  </button>
                  <span className={integration.status === "PUBLISHED" ? "dot on" : "dot off"} />
                </div>
              </li>
            ))}
          </ul>
        )}
        <Note>
          Administrators define integrations (endpoint, event scope, authorization) in coordination with the
          external system. Users can then enable published ones per number.
        </Note>
      </div>
    </>
  );
}

/** Provider status: the three external roles and whether each is configured. */
export function AdminProvidersPage(): JSX.Element {
  const { data: providers, error } = useApiData<ProviderStatus[]>("/api/v1/admin/providers");
  return (
    <>
      <Header title="Provider status" back="/admin" backLabel={"\u2190 Admin"} />
      <div className="content">
        <ErrorNote error={error} />
        <ul className="list">
          {(providers ?? []).map((provider) => (
            <li key={provider.role}>
              <div className="row-main">
                <div className="row-title">{provider.role.replace(/-/g, " ")}</div>
                <div className="row-sub">{provider.provider}</div>
              </div>
              <span className={provider.configured ? "badge active" : "badge disabled"}>
                {provider.configured ? "Configured" : "Not configured"}
              </span>
            </li>
          ))}
        </ul>
      </div>
    </>
  );
}

/** Paid but not provisioned: the reconciliation queue, with retry. */
export function AdminReconciliationPage(): JSX.Element {
  const { data: reconciliation, error, reload } =
    useApiData<Reconciliation[]>("/api/v1/admin/reconciliation");
  const [actionError, setActionError] = useState<unknown>(null);

  async function retry(intentId: string): Promise<void> {
    setActionError(null);
    try {
      await api.post(`/api/v1/admin/reconciliation/${intentId}/retry`);
      reload();
    } catch (caught) {
      setActionError(caught);
    }
  }

  return (
    <>
      <Header title="Reconciliation" back="/admin" backLabel={"\u2190 Admin"} />
      <div className="content">
        <ErrorNote error={error} />
        <ErrorNote error={actionError} />
        {reconciliation === null ? (
          <Loading />
        ) : reconciliation.length === 0 ? (
          <Empty>Nothing to reconcile.</Empty>
        ) : (
          <ul className="list">
            {reconciliation.map((intent) => (
              <li key={intent.intentId}>
                <div className="row-main">
                  <div className="row-title">{intent.productId}</div>
                  <div className="row-sub">{intent.failureReason}</div>
                  <div className="row-sub">{formatWhen(intent.createdAt)}</div>
                </div>
                <div className="row-actions">
                  <button type="button" onClick={() => retry(intent.intentId)}>
                    Retry
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
        <Note>These customers were charged. Retry provisioning, or refund at the provider.</Note>
      </div>
    </>
  );
}

/** Subscription lifecycle: the grace-expiry sweep. */
export function AdminLifecyclePage(): JSX.Element {
  const [actionError, setActionError] = useState<unknown>(null);
  const [sweepResult, setSweepResult] = useState<string | null>(null);

  async function sweep(): Promise<void> {
    setActionError(null);
    try {
      const released = await api.post<string[]>("/api/v1/admin/grace-sweep");
      setSweepResult(
        released.length === 0 ? "No numbers were past their grace period." : `Released ${released.length}.`
      );
    } catch (caught) {
      setActionError(caught);
    }
  }

  return (
    <>
      <Header title="Subscription lifecycle" back="/admin" backLabel={"\u2190 Admin"} />
      <div className="content">
        <ErrorNote error={actionError} />
        <button className="btn secondary" type="button" onClick={sweep}>
          Run the grace-expiry sweep now
        </button>
        {sweepResult ? <Note>{sweepResult}</Note> : null}
      </div>
    </>
  );
}

/** Recent inbound webhooks and their processing status. */
export function AdminWebhooksPage(): JSX.Element {
  const { data: webhooks, error } = useApiData<WebhookEvent[]>("/api/v1/admin/webhooks");
  return (
    <>
      <Header title="Inbound webhooks" back="/admin" backLabel={"\u2190 Admin"} />
      <div className="content">
        <ErrorNote error={error} />
        {webhooks === null ? (
          <Loading />
        ) : webhooks.length === 0 ? (
          <Empty>No webhooks received.</Empty>
        ) : (
          <ul className="list">
            {webhooks.slice(0, 25).map((event) => (
              <li key={event.id}>
                <div className="row-main">
                  <div className="row-title">{event.eventType}</div>
                  <div className="row-sub">
                    {event.source.toLowerCase().replace(/_/g, " ")} {"\u00B7"} {formatWhen(event.receivedAt)}
                  </div>
                  {event.errorMessage ? <div className="row-sub">{event.errorMessage}</div> : null}
                </div>
                <span
                  className={
                    event.processingStatus === "PROCESSED"
                      ? "badge active"
                      : event.processingStatus === "REJECTED"
                        ? "badge released"
                        : "badge disabled"
                  }
                >
                  {event.processingStatus.toLowerCase()}
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </>
  );
}

/** Placeholder: admin subscription management. Not yet implemented. */
export function AdminSubscriptionsPage(): JSX.Element {
  return (
    <>
      <Header title="Subscriptions" back="/admin" backLabel={"\u2190 Admin"} />
      <div className="content">
        <Empty>Subscription management is coming.</Empty>
        <Note>
          This area will let an admin edit a subscription: renewal, term dates, the product, the linked
          number, and cancellation.
        </Note>
      </div>
    </>
  );
}

/** Placeholder: admin number management. Not yet implemented. */
export function AdminNumbersPage(): JSX.Element {
  return (
    <>
      <Header title="Numbers" back="/admin" backLabel={"\u2190 Admin"} />
      <div className="content">
        <Empty>Number management is coming.</Empty>
        <Note>
          This area will list all numbers across users; opening one shows its assigned subscription and
          its assigned user.
        </Note>
      </div>
    </>
  );
}

/** Define (and optionally publish) an outbound integration. */
export function DefineIntegrationPage(): JSX.Element {
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [endpointUrl, setEndpointUrl] = useState("https://");
  const [authScheme, setAuthScheme] = useState("BEARER_TOKEN");
  const [eventScope, setEventScope] = useState("CALLS_AND_MESSAGES");
  const [error, setError] = useState<unknown>(null);

  async function save(publish: boolean): Promise<void> {
    setError(null);
    try {
      await api.post("/api/v1/admin/integrations", {
        name,
        endpointUrl,
        authScheme,
        eventScope,
        publish
      });
      navigate("/admin/integrations");
    } catch (caught) {
      setError(caught);
    }
  }

  return (
    <>
      <Header title="Define integration" back="/admin/integrations" backLabel={"\u2190 Integrations"} />
      <div className="content">
        <div className="field">
          <label htmlFor="intName">Name</label>
          <input
            id="intName"
            value={name}
            placeholder="e.g. Acme CRM"
            onChange={(event) => setName(event.target.value)}
          />
        </div>
        <div className="field">
          <label htmlFor="intUrl">Webhook endpoint (external)</label>
          <input id="intUrl" value={endpointUrl} onChange={(event) => setEndpointUrl(event.target.value)} />
        </div>
        <div className="field">
          <label htmlFor="intAuth">Authorization scheme</label>
          <select id="intAuth" value={authScheme} onChange={(event) => setAuthScheme(event.target.value)}>
            <option value="BEARER_TOKEN">Bearer token</option>
            <option value="API_KEY_HEADER">API key header</option>
            <option value="HMAC_SIGNATURE">HMAC signature</option>
          </select>
        </div>
        <div className="field">
          <label htmlFor="intScope">Events pushed</label>
          <select id="intScope" value={eventScope} onChange={(event) => setEventScope(event.target.value)}>
            <option value="CALLS_AND_MESSAGES">Calls + messages</option>
            <option value="CALLS_ONLY">Calls only</option>
            <option value="MESSAGES_ONLY">Messages only</option>
          </select>
        </div>
        <Note>
          The user supplies their own recipient token when enabling this integration; the scheme decides
          where that token is placed on each push.
        </Note>
        <ErrorNote error={error} />
        <button className="btn primary" type="button" onClick={() => save(true)}>
          Publish integration
        </button>
        <button className="btn secondary" type="button" onClick={() => save(false)}>
          Save as draft
        </button>
      </div>
    </>
  );
}
